package com.fireavert.react.active_events

import com.fireavert.cache.client.Preferences
import com.fireavert.events.active_events.data_models.ActiveEventWithActions
import com.fireavert.events.active_events.data_models.ActiveSensorType
import com.fireavert.events.shared.data_models.models.EventLinkedActionType
import com.fireavert.menu.menu_navigation.client.RoutePaths
import com.fireavert.react.pages.dashboard.styles.EventsStylesV2
import com.fireavert.utilities.getKoinInstance
import com.fireavert.utils.InstantTimeStringFormatter
import kotlinx.datetime.Instant
import mui.material.*
import react.FC
import react.Props
import react.ReactNode
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.span
import react.router.dom.Link


external interface ActiveEventsRowV2Props : Props {
    var event: ActiveEventWithActions
}

val ActiveEventsRowV2 = FC<ActiveEventsRowV2Props> { props ->
    val preferences = getKoinInstance<Preferences>()

    div {
        className = EventsStylesV2.eventsTableRow
        div {
            className = EventsStylesV2.eventsTableRowCell
            +InstantTimeStringFormatter.formatC(
                Instant.fromEpochSeconds(props.event.eventTimestamp),
                preferences.timeZone
            )
        }
        div {
            className = EventsStylesV2.eventsTableRowCellLeftAlign
            Link {
                to = "${RoutePaths.properties}/${props.event.propertyId}"
                +props.event.propertyName
            }
        }
        div {
            className = EventsStylesV2.eventsTableRowCellLeftAlign
            Link {
                to = "${RoutePaths.properties}/${props.event.propertyId}/units/${props.event.unitId}"
                +props.event.unitNumber
            }
        }
        div {
            className = EventsStylesV2.eventsTableRowCell
            ActiveEventsStatusIconSet {
                activeSensors = props.event.activeSensors
            }
        }
        div {
            className = EventsStylesV2.eventsTableRowCell
            AutomatedActionsTakenIconSet {
                automatedActionsTaken = props.event.actionsTaken
            }
        }
        div {
            className = EventsStylesV2.eventsTableRowCell
            if (props.event.activeSensors.contains(ActiveSensorType.Shutoff) && !props.event.activeSensors.contains(
                    ActiveSensorType.Offline)) {
                span {
                    className = EventsStylesV2.rebootDeviceButton
                    Link {
                        to = "${RoutePaths.properties}/${props.event.propertyId}/reboot_device/${props.event.deviceId}"
                        Tooltip {
                            title = ReactNode("Reboot the FireAvert Pro device")
                            placement = mui.material.TooltipPlacement.top
                            Button {
                                color = ButtonColor.error
                                variant = ButtonVariant.contained
                                +"Reboot"
                            }
                        }
                    }
                }
            }
            else {
                span {
                    className = EventsStylesV2.notReadyToReboot
                    +"Reboot"
                }
            }
        }
    }
}

external interface ActiveEventsV2StatusIconSetProps : Props {
    var activeSensors: Set<ActiveSensorType>
}

val ActiveEventsStatusIconSet = FC<ActiveEventsV2StatusIconSetProps> { props ->
    div {
        className = EventsStylesV2.eventsStatusIconSetContainer

        if (props.activeSensors.contains(ActiveSensorType.Smoke)) {
            Tooltip {
                title = ReactNode("A smoke event is happening!")
                img {
                    src = "/static/active_event_smoke_icon_active.svg"
                }
            }
        } else {
            img {
                src = "/static/active_event_smoke_icon.svg"
            }
        }

        if (props.activeSensors.contains(ActiveSensorType.Water)) {
            Tooltip {
                title = ReactNode("A water leak is happening!")
                placement = TooltipPlacement.top
                img {
                    src = "/static/active_event_leak_icon_active.svg"
                }
            }
        } else {
            img {
                src = "/static/active_event_leak_icon.svg"
            }
        }

        if (props.activeSensors.contains(ActiveSensorType.Tamper)) {
            Tooltip {
                title = ReactNode("A smoke alarm is tampered with!")
                placement = TooltipPlacement.top
                img {
                    src = "/static/active_event_tamper_icon_active.svg"
                }
            }
        } else {
            img {
                src = "/static/active_event_tamper_icon.svg"
            }
        }

        if (props.activeSensors.contains(ActiveSensorType.Offline)) {
            Tooltip {
                title = ReactNode("A device is offline!")
                placement = TooltipPlacement.top
                img {
                    src = "/static/active_event_offline_icon_active.svg"
                }
            }
        } else {
            img {
                src = "/static/active_event_offline_icon.svg"
            }
        }
    }
}

external interface AutomatedActionsTakenIconSetProps : Props {
    var automatedActionsTaken: Set<EventLinkedActionType>
}

val AutomatedActionsTakenIconSet = FC<AutomatedActionsTakenIconSetProps> { props ->
    div {
        className = EventsStylesV2.automatedActionsTakenIconSetContainer
        if (props.automatedActionsTaken.contains(EventLinkedActionType.StoveShutoff)) {
            Tooltip {
                title = ReactNode("Stove shutoff action taken")
                placement = TooltipPlacement.top
                img {
                    src = "/static/automated_actions_stove_shutoff_icon_active.svg"
                }
            }
        } else {
            img {
                src = "/static/automated_actions_stove_shutoff_icon.svg"
            }
        }
        if (props.automatedActionsTaken.contains(EventLinkedActionType.TextAlert)) {
            Tooltip {
                title = ReactNode("Message alert was sent out")
                placement = TooltipPlacement.top
                img {
                    src = "/static/automated_actions_text_alerts_icon_active.svg"
                }
            }
        } else {
            img {
                src = "/static/automated_actions_text_alerts_icon.svg"
            }
        }
    }
}